<template>
  <div class="event-cal">
    <Breadcrumbs :crumbs="crumbs" />
    <div class="event-cal-container">
      <div class="event-cal-sidebar">
        <EventsFiltersSelectors
          v-if="screenSize >= 1280"
          @move-focus="focusOnCurrentMonth"
        />
      </div>
      <div class="event-cal-content">
        <div class="event-cal-content-toolbar">
          <EventsToolbarHeader class="toolbar-header" />
          <EventsToolbarActions
            ref="toolbarActions"
            :is-mobile="isMobile"
            :filters-expanded="filtersVisible"
            @change-view="changeView"
            @collapse-menu="filtersVisible = !filtersVisible"
          />
        </div>
        <hr class="mobile-border" />
        <div
          v-if="filtersVisible && screenSize < 1280"
          class="event-cal-mobile-filters"
        >
          <EventsFiltersSelectors />
        </div>
        <div class="event-cal-content-wrapper">
          <LoadingIndicator v-if="loading" />
          <component
            :is="currentView"
            v-else
            ref="currentViewRef"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch, nextTick, onMounted } from 'vue';
import { useRoute } from 'vue-router';
import { navigateTo } from '#imports';
import { getSpecificListOrCalView } from './components/helpers/events.helper.js';
import Breadcrumbs from '../global/Breadcrumbs.vue';
import LoadingIndicator from '../global/LoadingIndicator.vue';
import EventsToolbarHeader from './components/EventsToolbarHeader.vue';
import EventsToolbarActions from './components/EventsToolbarActions.vue';
import EventsFiltersSelectors from './components/EventsFiltersSelectors.vue';
import EventsSearchView from './components/EventViews/EventsSearchView.vue';
import EventsListView from './components/EventViews/EventsListView.vue';
import EventsCalendarView from './components/EventViews/EventsCalendarView.vue';
import { useScreenWidth } from '../../composables/useScreenWidth.js';
import { useEventsStore } from '../../stores/eventsStore.js';
import { useMainStore } from '../../stores/mainStore.js';

defineOptions({ name: 'EventListContainer' });

const eventViewComponents = {
  EventsSearchView,
  EventsListView,
  EventsCalendarView,
};

const props = defineProps({
  loading: Boolean,
  extraCrumb: {
    type: Object,
    default: null,
  },
});

const eventsStore = useEventsStore();
const mainStore = useMainStore();
const route = useRoute();
const { screenSize } = useScreenWidth();

const currentViewRef = ref(null);
const filtersVisible = ref(true);

const currentViewName = computed(() => eventsStore.currentView);
const currentView = computed(() => eventViewComponents[currentViewName.value] ?? EventsListView);
const queryParams = computed(() => eventsStore.queryParams ?? {});
const pathPrefix = computed(() => mainStore.pathPrefix ?? '');
const globals = computed(() => mainStore.globals ?? {});
const getCurrentMonth = computed(() => eventsStore.getCurrentMonth);

const crumbs = computed(() => {
  const path = pathPrefix.value;
  const list = [
    { name: globals.value?.info?.school_name, link: `${path}/` },
    { name: 'Events', link: `${path}/events` },
  ];
  if (props.extraCrumb) {
    list.push({ name: props.extraCrumb.name, link: `${path}/${props.extraCrumb.slug}` });
  }
  return list;
});

const isMobile = computed(() => screenSize.value < 601);

watch(isMobile, (val) => {
  if (val && currentViewName.value === 'EventsCalendarView') {
    eventsStore.changeEventView('LIST_VIEW');
  }
});

onMounted(() => {
  nextTick(() => {
    if (isMobile.value) {
      eventsStore.changeEventView('LIST_VIEW');
      navigateTo({
        query: {
          ...route.query,
          view: getSpecificListOrCalView(eventsStore),
        },
      });
    }
  });
});

async function changeView(viewName, { id } = {}) {
  eventsStore.changeEventView(viewName);
  if (viewName === 'LIST_VIEW') {
    eventsStore.updateDateFilters({
      month: getCurrentMonth.value,
      target: id ?? null,
    });
  }
  return navigateTo({
    query: {
      ...route.query,
      id: undefined,
      view: getSpecificListOrCalView(eventsStore),
    },
  });
}

function focusOnCurrentMonth() {
  if (currentViewName.value === 'EventsListView' && queryParams.value?.view === 'list-month') {
    const el = currentViewRef.value?.$el;
    const monthTabs = el
      ? Array.from(el.querySelectorAll('button')).filter((button) => button.attributes['aria-selected'])
      : [];
    const activeMonthTab = monthTabs.find((tab) => tab.ariaSelected === 'true');
    activeMonthTab?.focus();
  } else {
    document.getElementById('EventsCalendarView')?.focus();
  }
}
</script>

<style scoped lang="scss">
@use '../global/scss/mixins/flex_mixin';
@use '../global/scss/mixins/breakpoints';

/* Page Layout */
.event-cal {
  min-height: 80vh;
  @include flex_mixin.flex-column;

  .event-cal-container {
    @include flex_mixin.flex-column;
    flex: 1;
    padding: 0 20px;
    margin-bottom: 40px;
    box-sizing: border-box;
  }

  .event-cal-sidebar {
    border-right: 1px solid rgba(0, 73, 144, 0.1);
    display: none;
  }

  .event-cal-content {
    flex: 1;
    @include flex_mixin.flex-column;
    margin-left: 38px;

    .event-cal-content-toolbar {
      @include flex_mixin.flex-column;
    }

    .event-cal-mobile-filters {
      @include flex_mixin.flex-column;
    }

    .event-cal-content-wrapper {
      flex: 1;
      z-index: 0;
      @include flex_mixin.flex-column;

      .event-cal__content {
        @include flex_mixin.flex-column;
      }
    }
  }
  @include breakpoints.screen('xs') {
    .event-cal-content {
      margin-left: 0;
      hr.mobile-border {
        border: 1px solid #0e315c0d;
        width: 90vw;
        margin: 24px 0;
      }
      .event-cal-content-toolbar {
        .toolbar-header {
          @include flex_mixin.flex-row;
          align-items: center;
        }
      }
    }
  }
  @include breakpoints.screen('sm') {
    .event-cal-content {
      margin: 0 12px;
    }
  }
  @include breakpoints.screen('md') {
    .event-cal-content {
      margin-left: 24px;
      padding-left: 0;
      margin-right: 10px;
      .event-cal-content-toolbar {
        @include flex_mixin.flex-row;
        justify-content: space-between;
      }
    }
  }
  @include breakpoints.screen('>=lg') {
    .event-cal-container {
      @include flex_mixin.flex-row;
      flex: 1;
      padding: 0 40px;
      box-sizing: border-box;
    }

    .event-cal-sidebar {
      @include flex_mixin.flex-column;
      max-width: 220px;
      min-width: 220px;
      padding-top: 52px;
      padding-right: 40px;
    }

    .event-cal-content {
      padding-top: 20px;
      padding-left: 16px;

      .event-cal-mobile-filters {
        display: none;
      }

      .event-cal-content-toolbar {
        @include flex_mixin.flex-row;
        justify-content: space-between;
      }
    }
  }
  @include breakpoints.screen('>=sm') {
    .mobile-border {
      display: none;
    }
  }
}
</style>
